import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateProduct, Product, UpdateProduct } from '@thro-types/leihbar';
import { ListResponse } from '@thro-types/users';

import { AxiosError, AxiosRequestConfig } from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  cloneProduct,
  createProduct,
  deleteProduct,
  getProduct,
  getProducts,
  updateProduct
} from 'api/leihbar';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

export const useProductsQuery = (config?: AxiosRequestConfig) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const query = useQuery<ListResponse<Product>, Error>({
    queryKey: ['products', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      return getProducts(config);
    },
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (query.error) {
      dispatch(
        AlertActions.error(`${t(translations.pages.leihbar.product.alerts.error.getProducts())}`)
      );
      navigate('/', { replace: true });
    }
  }, [dispatch, query.error, t, navigate]);

  return query;
};

export const useProductQuery = (id: number, config?: AxiosRequestConfig) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const query = useQuery<Product, Error>({
    queryKey: ['products', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      return getProduct(id, config);
    },
    enabled: id > 0,
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (query.error) {
      if (query.error instanceof AxiosError && query.error.response?.status === 404) {
        dispatch(
          AlertActions.error(`${t(translations.pages.leihbar.product.alerts.error.notFound())}`)
        );
      } else {
        dispatch(
          AlertActions.error(`${t(translations.pages.leihbar.product.alerts.error.openFailed())}`)
        );
      }
      navigate('/leihbar', { replace: true });
    }
  }, [dispatch, query.error, t, navigate]);

  return query;
};

export const useProductMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['products'];

  const create = useMutation<Product, Error, CreateProduct>({
    mutationFn: async args => {
      return await createProduct(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<Product, Error, { id: number; data: UpdateProduct }>({
    mutationFn: async args => {
      return await updateProduct(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<Product, Error, number>({
    mutationFn: async args => {
      return await deleteProduct(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const clone = useMutation<Product, Error, number>({
    mutationFn: async args => {
      return await cloneProduct(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { create, update, deletion, clone } as const;
};
